import React, { useEffect, useState } from 'react';
import Select, { components } from 'react-select';
import { black_icons } from '../../icons';
import { optimizeCanvasState } from '../../Utils/canvasUtils';

const ShareModal = ({ show, onHide, onSubmit, selectedModel, selectedFabric, canvasState, settings, designerRef, canvasRef }) => {
    // State for the current step
    const [step, setStep] = useState(1);

    // Form fields state
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [priceRequest, setPriceRequest] = useState(false);
    const [designName, setDesignName] = useState('');
    const [note, setNote] = useState('');


    // Select component state
    const [countryOptions, setCountryOptions] = useState([]);
    const [flatCountryOptions, setFlatCountryOptions] = useState([]);
    const [isCountryMenuOpen, setIsCountryMenuOpen] = useState(false);

    // Submission state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});


    useEffect(() => {
        // Reset to step 1 when the modal is shown
        if (show) {
            setStep(1);
            setErrors({});
        }
    }, [show]);


    useEffect(() => {
        if (!settings) return;

        const default_option = settings.countries.default;
        let defaultCountryValue = null;

        const options = Object.entries(settings.countries.items).map(([key, value]) => {
            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                return {
                    label: key,
                    options: Object.entries(value).map(([subKey, subValue]) => {
                        if (subKey === default_option) {
                            defaultCountryValue = { value: subKey, label: subValue, type: 'optgroup' };
                        }
                        return {
                            value: subKey,
                            label: subValue,
                            type: 'optgroup',
                            groupLabel: key // Add group label to support searching by country name
                        };
                    })
                };
            } else {
                if (key === default_option) {
                    defaultCountryValue = { value: key, label: value, type: 'option' };
                }
                return {
                    value: key,
                    label: value,
                    type: 'option'
                };
            }
        });

        setCountryOptions(options);
        if (defaultCountryValue) {
            setSelectedCountry(defaultCountryValue.value);
        }


        const allOptions = options.flatMap(option =>
            option.options ? option.options : option
        );
        setFlatCountryOptions(allOptions);

    }, [settings]);

    const handleCountryChange = (selectedOption) => {
        if (selectedOption) {
            setSelectedCountry(selectedOption.value);
            setErrors(prev => ({ ...prev, country: null }));
        } else {
            setSelectedCountry(null);
        }
    };

    const handleInputChange = (inputValue, { action }) => {
        if (action === 'input-change') {
            const matchedOption = flatCountryOptions.find(
                option => option.label.toLowerCase() === inputValue.toLowerCase()
            );

            if (matchedOption && matchedOption.value !== selectedCountry) {
                handleCountryChange(matchedOption);
                setTimeout(() => setIsCountryMenuOpen(false), 0);
            }
        }
    };

    const handleDownloadPictures = () => {
        if (designerRef && designerRef.current) {
            const name = (selectedModel && selectedFabric) ? `${selectedModel.name} - ${selectedFabric.name}` : "MyDesign";
            designerRef.current.downloadPictures(designName || name, [0]);
        } else {
            console.log("Downloading pictures...");
        }
    };

    const Placeholder = (props) => (
        <components.Placeholder {...props}>
            Choose
        </components.Placeholder>
    );

    // Styles for the react-select component
    const customStyles = {
        control: (provided) => ({ ...provided }),
        singleValue: (provided) => ({ ...provided }),
        input: (provided) => ({ ...provided }),
        menu: (provided) => ({ ...provided }),
        option: (provided, state) => ({
            ...provided,
            paddingLeft: state.data.type === 'option' ? '10px' : '15px',
        }),
        menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
        groupHeading: (provided) => ({ ...provided }),
    };

    const validateStep1 = () => {
        const newErrors = {};
        if (!designName.trim()) newErrors.designName = 'Design name is required.';
        if (!name.trim()) newErrors.name = 'Your name is required.';
        if (!email.trim()) {
            newErrors.email = 'Email is required.';
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            newErrors.email = 'Email is invalid.';
        }
        if (!selectedCountry) newErrors.country = 'Country is required.';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    const handleNext = () => {
        if (validateStep1()) {
            setStep(2);
        }
    };

    const handleBack = () => {
        setStep(1);
    };


    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validateStep1()) {
            setStep(1);
            return;
        }

        setIsSubmitting(true);
        setErrors({});

        const formData = {
            balloonName: designName,
            name: name,
            email: email,
            country: selectedCountry,
            phone: phoneNumber,
            priceRequest: priceRequest,
            note: note,
        };

        let hasEmptyFields = false;
        for (const field in formData) {
            if (['priceRequest', 'phone', 'note'].includes(field)) continue;
            if (!formData[field]) {
                hasEmptyFields = true;
                break;
            }
        }

        if (hasEmptyFields) {
            alert('Please fill in all required fields.');
        } else {
            const hexToIdMap = {};
            selectedFabric.colors.items.forEach(color => {
                hexToIdMap[color.hex] = color.id;
            });

            const nomexHexToIdMap = {};
            selectedFabric.nomex.items.forEach(color => {
                nomexHexToIdMap[color.hex] = color.id;
            });

            let nomexColor;
            const nomexRowsCount = canvasState.nomexBottomRows || 1;

            const modifiedEnvelopeGrid = canvasState.envelopeGrid.map((row, idx) => {
                if (idx < nomexRowsCount) {
                    if (idx === 0) {
                        nomexColor = nomexHexToIdMap[row[0]] || row[0];
                    }
                    return row.map(hexValue => nomexHexToIdMap[hexValue] || hexValue);
                } else {
                    return row.map(hexValue => hexToIdMap[hexValue] || hexValue)
                }
            }
            );

            const pictures = await designerRef.current.getPictures([0, 1, 2, 3, 4, 5, 6, 7]).catch(error => {
                console.error("Error taking pictures:", error);
                return [];
            });

            const envelope2D = await canvasRef.current.getEnvelope2D();
            const shareEnvelope2D = await canvasRef.current.getShareModalEnvelope2D();

            const preOptimizedState = {
                ...canvasState,
                envelopeGrid: modifiedEnvelopeGrid,
                nomexColor: nomexColor,
                loadlines: canvasState.loadlines || '#FFFFFF'
            };

            // Don't send the generic label to the backend
            delete preOptimizedState.label;

            const optimizedState = optimizeCanvasState(preOptimizedState);

            const data = {
                formData: formData,
                canvasState: optimizedState,
                selectedModelID: selectedModel.id,
                selectedFabricID: selectedFabric.id,
                images: pictures,
                envelope2D: envelope2D,
                shareEnvelope2D: shareEnvelope2D
            };

            const formDataToSend = new FormData();
            for (const key in data) {
                formDataToSend.append(key, ['selectedModelID', 'selectedFabricID'].includes(key) ? data[key] : JSON.stringify(data[key]));
            }

            fetch(' https://kubicekballoons.com/en/designer-api/design/', {
                method: 'POST',
                body: formDataToSend
            })
                .then(response => response.json())
                .then(data => {
                    const url = new URL(window.location);
                    url.searchParams.set('id', data.id);
                    window.history.pushState({}, '', url);
                    onHide(true);
                    setIsSubmitting(false);
                    setDesignName('');
                    setPhoneNumber('');
                    setPriceRequest(true);
                    setName('');
                    setEmail('');
                    setSelectedCountry(null);
                })
                .catch(error => {
                    console.error('Error:', error);
                    setIsSubmitting(false);
                });
        };
    };

    // Custom filter option to search by label or group label (country name)
    const customFilterOption = (option, inputValue) => {
        const { label, data } = option;
        const input = inputValue.toLowerCase();

        // Check if the option label matches
        const labelMatches = label.toLowerCase().includes(input);

        // Check if the group label (country name) matches, if it exists
        const groupLabelMatches = data.groupLabel && data.groupLabel.toLowerCase().includes(input);

        return labelMatches || groupLabelMatches;
    };

    const renderStep1 = () => (
        <>
            <div className='mdl-info'>
                <div className='mdl-text'>GET COMPLETE BALLOON VISUAL</div>
                <div className='mdl-sub-text'>Hi-res PDF with colour palette and editable link.</div>
            </div>

            <div className='mdl-row'>
                <div className="form-group">
                    <div className="label-row">
                        <label className="mdl-lbl" htmlFor="formBasicBalloonName">DESIGN NICKNAME</label>
                        {errors.designName && <p className="error-text">{errors.designName}</p>}
                    </div>
                    <input
                        type="text"
                        className="form-control"
                        id="formBasicBalloonName"
                        placeholder={(selectedModel && selectedFabric) ? `${selectedModel.name} - ${selectedFabric.name}` : "SELECT MODEL"}
                        required
                        name="desing"
                        autoComplete='off'
                        value={designName}
                        onChange={(e) => {
                            setDesignName(e.target.value);
                            setErrors(prev => ({ ...prev, designName: null }));
                        }}
                    />
                </div>
            </div>

            <div className="mdl-row">
                <div className="form-group">
                    <div className="label-row">
                        <label className="mdl-lbl" htmlFor="formBasicName">YOUR NAME</label>
                        {errors.name && <p className="error-text">{errors.name}</p>}
                    </div>
                    <input
                        type="text"
                        className="form-control"
                        id="formBasicName"
                        placeholder="Name"
                        required
                        name="name"
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                            setErrors(prev => ({ ...prev, name: null }));
                        }}
                    />
                </div>
                <div className="form-group">
                    <div className="label-row">
                        <label className="mdl-lbl" htmlFor="formBasicEmail">E-MAIL</label>
                        {errors.email && <p className="error-text">{errors.email}</p>}
                    </div>
                    <input
                        type="email"
                        className="form-control"
                        id="formBasicEmail"
                        placeholder="example@dot.com"
                        required
                        name="email"
                        value={email}
                        onChange={(e) => {
                            setEmail(e.target.value);
                            setErrors(prev => ({ ...prev, email: null }));
                        }}
                    />
                </div>
            </div>

            <div className="mdl-row">
                <div className="form-group">
                    <div className="label-row">
                        <label className="mdl-lbl" htmlFor="country">COUNTRY</label>
                        {errors.country && <p className="error-text">{errors.country}</p>}
                    </div>
                    <Select
                        options={countryOptions}
                        isClearable={true}
                        onChange={handleCountryChange}
                        value={flatCountryOptions.find(option => option.value === selectedCountry) || null}
                        components={{ Placeholder }}
                        styles={customStyles}
                        className="basic-multi-select"
                        classNamePrefix="select"
                        inputId="country"
                        name="country"
                        required
                        onInputChange={handleInputChange}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        menuIsOpen={isCountryMenuOpen}
                        onMenuOpen={() => setIsCountryMenuOpen(true)}
                        onMenuClose={() => setIsCountryMenuOpen(false)}
                        filterOption={customFilterOption}
                    />
                </div>
                <div className="form-group">
                    <label className="mdl-lbl" htmlFor="formBasicPhone">PHONE NUMBER (OPTIONAL)</label>
                    <input type="tel" className="form-control" id="formBasicPhone" placeholder="Enter Phone Number" value={phoneNumber} name="phone" onChange={(e) => setPhoneNumber(e.target.value)} />
                </div>
            </div>

            <div className="mdl-sub-text">
                <input type="checkbox" checked={priceRequest} onChange={() => setPriceRequest(!priceRequest)} /> Request price
            </div>
            <div className="mdl-sub-text">
                By clicking "GO AHEAD" you agree to our&nbsp;<a href="https://kubicekballoons.com/en/privacy-policy/" target="_blank" rel="noopener noreferrer" className="gdpr-link"> privacy policy</a>.
            </div>

            <div className="mdl-row">
                <button type="button" onClick={handleNext} className="mdl-btn primary">
                    GO AHEAD
                </button>
                <button type="button" className="mdl-btn secondary" onClick={() => onHide(false)}>
                    CANCEL
                </button>
            </div>
        </>
    );

    const renderStep2 = () => (
        // This wrapper will act as a flex container to structure the content of step 2
        <div className="step-wrapper">
            <div className='mdl-info'>
                <div className='mdl-text'>GET COMPLETE BALLOON VISUAL</div>
            </div>
            {/* This form group will grow to fill the available vertical space */}
            <div className="form-group form-group-grow">
                <label className="mdl-lbl" htmlFor="yourNote">YOUR NOTE</label>
                <textarea
                    id="yourNote"
                    // This class ensures the textarea fills its parent container
                    className="form-control h-full"
                    rows="4"
                    placeholder="I want price for basket as well..."
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                ></textarea>
            </div>



            <div className="mdl-row">
                <button type="submit" disabled={isSubmitting} className={`mdl-btn primary ${isSubmitting ? 'submitting' : ''}`}>
                    <p>{isSubmitting ? 'SUBMITTING...' : 'GET THE VISUAL'}</p>
                </button>
                <button type="button" disabled={isSubmitting} className="mdl-btn secondary" onClick={handleBack}>
                    BACK
                </button>
            </div>
        </div>
    );


    return (
        <div className="mdl backdrop-blur-[30px]" style={{ display: show ? 'flex' : 'none' }}>
            <div className="mdl-dia share-modal-dia">
                <form onSubmit={handleSubmit} className="mdl-form">
                    <div className="share-content-wrapper">
                        <div className={`share-slider step-${step}`}>
                            <div className="share-slide">
                                {renderStep1()}
                            </div>
                            <div className="share-slide">
                                {renderStep2()}
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ShareModal;
